/**
 * BPG Row Slider — Web Component
 *
 * On mobile (<= 768px) the row's product cards become a Swiper slider.
 * On desktop the cards are laid out as flex columns via CSS (display: contents),
 * so Swiper is destroyed. Swiper 11 is loaded globally in theme.liquid
 * (window.Swiper).
 */
class BpgRowSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.mq = window.matchMedia('(max-width: 768px)');
    this.onChange = this.sync.bind(this);
  }

  connectedCallback() {
    this.mq.addEventListener('change', this.onChange);
    this.sync();
  }

  disconnectedCallback() {
    this.mq.removeEventListener('change', this.onChange);
    this.destroy();
  }

  /* Init on mobile, destroy on desktop */
  sync() {
    if (this.mq.matches) {
      this.init();
    } else {
      this.destroy();
    }
  }

  init() {
    if (this.swiper) return;

    const SwiperCtor = /** @type {any} */ (window).Swiper;
    if (!SwiperCtor) return;

    this.swiper = new SwiperCtor(this, {
      slideClass: 'bpg__row-item',
      slidesPerView: 'auto',
      spaceBetween: 10,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      navigation: {
        prevEl: this.querySelector('.bpg__slider-arrow--prev'),
        nextEl: this.querySelector('.bpg__slider-arrow--next'),
      },
      scrollbar: {
        el: this.querySelector('.bpg__slider-progress'),
        draggable: true,
      },
    });
  }

  destroy() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }

  /* Recalculate after the row is revealed (deck activation) */
  refresh() {
    if (this.swiper) this.swiper.update();
  }
}

if (!customElements.get('bpg-row-slider')) {
  customElements.define('bpg-row-slider', BpgRowSlider);
}

/**
 * BPG Deck — Web Component
 *
 * On mobile the rows stack like a deck of cards: one card is active (expanded
 * with its full content) and the rest collapse to a 56px header that peeks
 * above it. Clicking a collapsed header brings that row to the front; the
 * others slide back, keeping their relative order.
 *
 * On desktop the deck is disabled and the rows flow normally.
 */
const BPG_DECK_PEEK = 56;

class BpgDeck extends HTMLElement {
  constructor() {
    super();
    this.enabled = false;
    /** @type {any[]} */
    this.rows = [];
    this.mq = window.matchMedia('(max-width: 768px)');
    this.onChange = this.sync.bind(this);
    this.onResize = this.debounce(() => {
      if (this.enabled) this.layout();
    }, 150);
  }

  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('.bpg__row'));
    if (this.rows.length <= 1) return;
    this.mq.addEventListener('change', this.onChange);
    window.addEventListener('resize', this.onResize);
    this.sync();
  }

  disconnectedCallback() {
    this.mq.removeEventListener('change', this.onChange);
    window.removeEventListener('resize', this.onResize);
    this.disable();
  }

  sync() {
    if (this.mq.matches) {
      this.enable();
    } else {
      this.disable();
    }
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.classList.add('bpg__rows--deck');

    this.rows.forEach((row, index) => {
      /* Flatten the Shopify block wrapper (if any) so rows position against
         .bpg__rows rather than their wrapper. */
      if (row.parentElement !== this) {
        row.parentElement.style.display = 'contents';
      }
      row.dataset.pos = String(index);
      row.classList.toggle('is-active', index === 0);
    });

    /* Delegated click — robust to DOM changes and works anywhere on a
       collapsed card's peek. Clicks on the active card are ignored by
       activate() (it early-returns), so its links/slider still work. */
    if (!this._clickBound) {
      this._clickBound = true;
      this.addEventListener('click', this.onClick.bind(this));
    }

    this.layout();
  }

  /** @param {any} event */
  onClick(event) {
    if (!this.enabled) return;
    const row = event.target.closest('.bpg__row');
    if (!row || this.rows.indexOf(row) === -1) return;

    /* The active card's View Products button toggles its product slider. */
    if (event.target.closest('.bpg__card--info__button') && Number(row.dataset.pos) === 0) {
      this.toggleProducts(row);
      return;
    }

    /* Otherwise a click brings a collapsed card to the front. */
    this.activate(row);
  }

  /* Smoothly collapse/expand the product slider (accordion-style height
     animation) and animate the deck height in parallel so the section below
     slides instead of jumping. */
  /** @param {any} row */
  toggleProducts(row) {
    const body = row.querySelector('.bpg__row-body');
    if (!body) return;

    const willHide = !row.classList.contains('is-products-hidden');

    if (willHide) {
      body.style.height = body.scrollHeight + 'px';
      body.getBoundingClientRect(); /* reflow */
      row.classList.add('is-products-hidden');
      body.style.height = '0px';
    } else {
      /* Measure the true full height with padding/opacity already restored
         (transitions off) so the open animation has a correct target and
         doesn't jump/pause at the end. */
      body.style.transition = 'none';
      row.classList.remove('is-products-hidden');
      body.style.height = 'auto';
      const full = body.scrollHeight;
      body.style.height = '0px';
      body.getBoundingClientRect(); /* commit collapsed state */
      body.style.transition = '';
      body.getBoundingClientRect(); /* commit restored transition */
      body.style.height = full + 'px';
      const onEnd = (/** @type {any} */ e) => {
        if (e.propertyName !== 'height') return;
        body.style.height = '';
        body.removeEventListener('transitionend', onEnd);
      };
      body.addEventListener('transitionend', onEnd);
    }

    this.layout();
  }

  disable() {
    if (!this.enabled) return;
    this.enabled = false;
    this.classList.remove('bpg__rows--deck');
    this.style.height = '';

    this.rows.forEach((row) => {
      row.style.transform = '';
      row.style.zIndex = '';
      row.classList.remove('is-active');
      delete row.dataset.pos;
      if (row.parentElement !== this) {
        row.parentElement.style.display = '';
      }
    });
  }

  /** @param {any} clickedRow */
  activate(clickedRow) {
    const clickedPos = Number(clickedRow.dataset.pos);
    if (clickedPos === 0) return;

    this.rows.forEach((row) => {
      const pos = Number(row.dataset.pos);
      if (row === clickedRow) {
        row.dataset.pos = '0';
      } else if (pos < clickedPos) {
        row.dataset.pos = String(pos + 1);
      }
    });

    this.rows.forEach((row) => {
      const isActive = Number(row.dataset.pos) === 0;
      row.classList.toggle('is-active', isActive);
      /* A freshly activated card always starts with its products shown */
      if (isActive) row.classList.remove('is-products-hidden');
    });

    this.layout();

    const active = this.rows.find((row) => Number(row.dataset.pos) === 0);
    const slider = active && active.querySelector('bpg-row-slider');
    if (slider && slider.refresh) slider.refresh();
  }

  layout() {
    const count = this.rows.length;
    let activeHeight = 0;

    this.rows.forEach((row) => {
      const pos = Number(row.dataset.pos);
      const y = (count - 1 - pos) * BPG_DECK_PEEK;
      row.style.transform = `translateY(${y}px)`;
      row.style.zIndex = String(count - pos);
      if (pos === 0) {
        /* Target height (info + slider, unless products are hidden) computed
           from scrollHeight so it's correct even mid-animation — lets the deck
           height transition in sync with the slider collapse/expand. */
        const info = row.querySelector('.bpg__row__info-mobile');
        const body = row.querySelector('.bpg__row-body');
        const productsHidden = row.classList.contains('is-products-hidden');
        activeHeight =
          (info ? info.offsetHeight : 0) +
          (productsHidden || !body ? 0 : body.scrollHeight);
      }
    });

    this.style.height = (count - 1) * BPG_DECK_PEEK + activeHeight + 'px';
  }

  /**
   * @param {Function} fn
   * @param {number} ms
   */
  debounce(fn, ms) {
    /** @type {any} */
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };
  }
}

if (!customElements.get('bpg-deck')) {
  customElements.define('bpg-deck', BpgDeck);
}
